import { decodeHTML } from "entities";
import React from "react";

import { memoize } from "@use/decorators";

import { ElementModel } from "@courses-pm2/api/src/ts/models/ElementModel";
import { GlossaryWordModel } from "@courses-pm2/api/src/ts/models/elements/GlossaryWordModel";
import { GridModel } from "@courses-pm2/api/src/ts/models/elements/GridModel";

import {
  Engine,
  getStandardPageCount,
  getTextContent,
} from "@courses-pm2/api/src/ts/utils";
import {
  CaptionedContentElementData,
  CaptionedContentElementModel,
} from "./CaptionedContentElementModel";

/**
 * Specifies the desired length of the preview text, given in number of
 * characters.
 */
const DESIRED_PREVIEW_TEXT_LENGTH = 150;

/**
 * An auto incremented id that's used to make sure each node within the feed
 * will have a unique key applied, so that we can suppress React warnings about
 * missing keys.
 */
let autoIncrementedId = 0;

/**
 * Specification of data needed to instantiate a new AccordionModel.
 */
export type RichtextData = {
  [K in
    | "autoCenter"
    | "collapsible"
    | "content"
    | "nested"
    | "solid"]: RichtextModel[K];
};

/**
 * Specification of the props that are supported on html nodes.
 */
export type RichtextHtmlNodeProps = {
  className?: string;
  colSpan?: number;
  href?: string;
  rowSpan?: number;
  start?: string;
  style?: { [key: string]: string };
  target?: string;
  type?: string; // tslint:disable-line no-reserved-keywords
  width?: string;
};

/**
 * Specification of the internal data format used to describe html nodes prior
 * to rendering them
 */
export type RichtextHtmlNode = {
  tagName: string;
  props: RichtextHtmlNodeProps;
  children: RichtextContentNode[] | RichtextContentNode;
};

/**
 * Specifies the different supported type of content nodes that can be rnedered
 * within this element.
 */
export type RichtextContentNode =
  | ElementModel
  | RichtextHtmlNode
  | string
  | null
  | undefined;

/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class RichtextModel extends CaptionedContentElementModel {
  /**
   * Specifies if the text within the component should be centered (ie. if a
   * text-align: center should be applied on the wrapper).
   */
  public readonly autoCenter?: boolean;

  /**
   * Specifies if the component should be collapsible (ie. start by rendering
   * only part of the content, allowing the user to expand the rest himself).
   */
  public readonly collapsible?: boolean;

  /**
   * Contains the content that should be rendered within this model, which is
   * going to be comprised by a mixed list of html content and element models.
   */
  public readonly content: RichtextContentNode[];

  /**
   * Will specify if the component has been nested within another Richtext
   * component (which will often mean that it should be handled slightly
   * different).
   */
  public readonly nested?: boolean;

  /**
   * in case the text component should be rendered with a background, then
   * this attribute may be set to true.
   */
  public readonly solid?: boolean;

  /**
   * Apply data given when a new model is being instantiated.
   */
  constructor(initial: RichtextData & CaptionedContentElementData) {
    super(initial);

    this.autoCenter = initial.autoCenter;
    this.collapsible = initial.collapsible;
    this.content = initial.content;
    this.nested = initial.nested;
    this.solid = initial.solid;
  }

  /**
   * Helper that'll transform the rendered content into a
   */
  @memoize()
  public render(): React.ReactNode[] {
    if (!this.engine) {
      throw new Error(
        "RichtextModel.render(): No rendering engine provided to this model.",
      );
    }

    return renderContentNodes(this.content, this.engine);
  }

  /**
   * Iterate over the list of nested elements, and attempt to extract a
   * preview image from one of the them if possible.
   */
  @memoize()
  public async getPreviewImageUrl(): Promise<string | undefined> {
    for (const content of this.content) {
      if (content instanceof ElementModel) {
        const previewImageUrl = await content.getPreviewImageUrl();

        if (previewImageUrl) {
          return previewImageUrl;
        }
      }
    }

    // If we get here, no preview images are available for this component
    return undefined;
  }

  /**
   * Helper that'll extract a raw text snippet from the html feed of the
   * component.
   */
  @memoize()
  public getPreviewText(): string {
    // Recursively extract text content from the component
    const text = decodeHTML(recursivelyExtractTextContent(this.content));

    // Extract a snippet of the content
    const cropAt = text.indexOf(" ", DESIRED_PREVIEW_TEXT_LENGTH);

    return cropAt !== -1 ? `${text.substr(0, cropAt)}...` : text;
  }

  /**
   * @inheritdoc
   *
   * Element links are able to contain RichTextNodes, so use the helper from
   * the RichtextModel to calculate standard pages for the content.
   */
  // tslint:disable-next-line: completed-docs
  @memoize()
  public getStandardPageCount(): number {
    return reduceRichTextNodeStandardPageCount(this.content);
  }
}

/**
 * Helper that'll iterate over the content of the component, making sure that it
 * is being transformed into React nodes that can be mounted directly.
 */
export function renderContentNodes(
  nodes: RichtextContentNode[] | RichtextContentNode,
  engine: Engine,
): React.ReactNode[] {
  if (!Array.isArray(nodes)) {
    // If we're rendering a single node, then just do so right away
    return [renderContentNode(nodes, engine)];
  }

  // ... Otherwise iterate over all of the nodes
  return nodes.map((node) => renderContentNode(node, engine));
}

/**
 * Helper that'll transform a single content node into a React node.
 */
function renderContentNode(
  node: RichtextContentNode,
  engine: Engine,
): React.ReactNode {
  if (!node) {
    // Ignore empty nodes
    return undefined;
  }

  if (node instanceof ElementModel) {
    // Render element models using the built in component map
    return React.createElement(
      React.Fragment,
      {
        key: ++autoIncrementedId,
      },
      engine.render(node),
    );
  }

  if (typeof node === "string") {
    // Wrap text nodes in the MathJaxUtil
    return node.trim()
      ? React.createElement(
          React.Fragment,
          {
            key: ++autoIncrementedId,
          },
          node,
        )
      : node;
  }

  // Some elements doesn't allow for children
  switch (node.tagName.toLowerCase()) {
    case "br":
    case "wbr":
      return React.createElement(node.tagName.toLowerCase(), {
        ...node.props,
        key: ++autoIncrementedId,
      });

    default:
      return React.createElement(
        node.tagName.toLowerCase(),
        {
          ...node.props,
          key: ++autoIncrementedId,
        },
        renderContentNodes(node.children, engine),
      );
  }
}

/**
 * Helper that'll recursively extract the text content of the given content
 * nodes.
 */
function recursivelyExtractTextContent(
  nodes: RichtextContentNode[] | RichtextContentNode,
): string {
  const text: string[] = [];

  // Iterate over all of the given nodes, and extract text from them
  const nodeArray = Array.isArray(nodes) ? nodes : [nodes];

  for (const node of nodeArray) {
    if (!node) {
      // Ignore empty nodes
      continue;
    }

    if (typeof node === "string") {
      // Push text nodes to output
      text.push(node);

      continue;
    }

    if (node instanceof GlossaryWordModel) {
      // Push nested text content from GlossaryWordModels within this
      // component
      text.push(recursivelyExtractTextContent(node.content));

      continue;
    }

    if (node instanceof GridModel) {
      // Push nested text content from GridModels within this component
      for (const column of node.columns) {
        text.push(recursivelyExtractTextContent(column.content));
      }

      continue;
    }

    if (!(node instanceof ElementModel)) {
      // Recursively extract text content from nested html nodes
      text.push(recursivelyExtractTextContent(node.children));
    }
  }

  // Return combined text content
  return text.filter((chunk) => chunk).join(" ");
}

/**
 * Reduce a list of richtext nodes to the calculated standard page count based
 * on their raw textual content.
 */
export function reduceRichTextNodeStandardPageCount(
  nodes: RichtextContentNode[],
): number {
  return getStandardPageCount(
    getTextContent(recursivelyExtractTextContent(nodes)),
  );
}
